import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { Team, Pokemon } from '../../../types';
import { getPokemonDexUrl } from '../../../shared/utils/pokemonDexUrl';
import './PlayerRow.scss';

interface PlayerRowProps {
  teams: Team[];
  numPlayers: number;
  highestBidderId?: string | null;
  wsConnected?: boolean;
  currentUserId?: string | null;
  highestBid?: number;
  auctionCompleted?: boolean;
  hideMoney?: boolean;
  positionColors?: boolean;
  /**
   * Identifies the P1 team when `positionColors` is set. Cards are matched
   * against this rather than against their position in the row, so a reorder
   * can never paint a player with the other player's colour.
   */
  positionOneId?: string | null;
  highlightId?: string | null;
  equalWidth?: boolean;
}

interface SortableItemProps {
  team: any;
  highestBidderId?: string | null;
  wsConnected?: boolean;
  animatingId: string | null;
  autoSortByFunds: boolean;
  getIconName: (name: string) => string;
  auctionCompleted?: boolean;
  hideMoney?: boolean;
  positionColorClass?: string;
  highlighted?: boolean;
  currentUserId?: string | null;
  playerCardColor?: string;
  positionColors?: boolean;
}

const SortableItem: React.FC<SortableItemProps> = ({ team, highestBidderId, wsConnected, animatingId, autoSortByFunds, getIconName, auctionCompleted, hideMoney, positionColorClass, highlighted, currentUserId, playerCardColor, positionColors }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: team.dragId,
  });

  // Position-coloured cards are locked to their P1/P2 slot, so they must not
  // respond to drag gestures at all — otherwise the row would let the user
  // pick a card up only to snap it straight back.
  const draggable = !positionColors;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: draggable && !autoSortByFunds ? 'grab' : 'default',
  };

  // Position-colored slots are 1v1 P1/P2 cards, which always use their fixed
  // blue/red styling; the custom player card color from settings is only for
  // auction drafts.
  const isMyCard = !positionColors && !team.isPlaceholder && playerCardColor && team.user_id === currentUserId;
  const colorVarStyle = isMyCard ? { ['--player-card-color' as any]: playerCardColor } : {};

  const playerName = team.isPlaceholder ? null : (team.global_name || team.username || team.user_id);
  const isFilled = !team.isPlaceholder;
  const readinessClass = isFilled && team.ready ? 'player-ready' : 'player-not-ready';
  const teamMoney = team.budget_remaining ?? 0;
  const zeroMoneyClass = isFilled && !hideMoney && teamMoney === 0 ? 'player-zero-money' : '';
  const playerStateClass = isFilled
    ? (zeroMoneyClass || readinessClass)
    : 'player-open';
  const wonPokemon = team.auctions_won ?? team.pokemon ?? [];
  const disconnectedClass = !wsConnected ? 'player-disconnected' : '';

  const cardContent = (
    <>
      <div className="auction-player-name">
        {playerName || 'Open Slot'}
      </div>
      {!hideMoney && (
        <div className="auction-player-money">${teamMoney.toLocaleString()}</div>
      )}
      <div className="auction-player-icons">
        {wonPokemon.map((pokemon: Pokemon) => (
          <a
            key={`${pokemon.name}-${pokemon.form ?? 'base'}`}
            href={getPokemonDexUrl(pokemon.name, pokemon.form)}
            target="_blank"
            rel="noopener noreferrer"
            title={`Open ${pokemon.name} in Pokédex (new tab)`}
            onClick={(e) => e.stopPropagation()}
            style={{ display: 'inline-flex', textDecoration: 'none' }}
          >
            <img
              src={`/MiniIcons/${getIconName(pokemon.name)}.png`}
              alt={pokemon.name}
              className="auction-player-icon"
              loading="lazy"
            />
          </a>
        ))}
      </div>
    </>
  );

  const colorClass = positionColorClass ? ` ${positionColorClass}` : '';
  const baseClasses = `auction-player-box ${playerStateClass} ${team.user_id === highestBidderId && !auctionCompleted ? 'highest-bidder' : ''} ${disconnectedClass} ${team.user_id === animatingId ? 'player-bidding' : ''}${colorClass}${highlighted ? ' player-turn' : ''}`;

  if (autoSortByFunds) {
    return (
      <motion.div
        ref={setNodeRef}
        {...attributes}
        className={baseClasses}
        style={colorVarStyle}
        layout
        layoutId={team.dragId}
        transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
      >
        {cardContent}
      </motion.div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={{ ...style, ...colorVarStyle }}
      {...attributes}
      {...(draggable ? listeners : {})}
      className={baseClasses}
    >
      {cardContent}
    </div>
  );
};

const PlayerRow: React.FC<PlayerRowProps> = ({ teams, numPlayers, highestBidderId, wsConnected = true, currentUserId, highestBid, auctionCompleted, hideMoney, positionColors, positionOneId, highlightId, equalWidth }) => {
  const [animatingId, setAnimatingId] = React.useState<string | null>(null);
  const isInitial = React.useRef(true);

  // Local state to manage the visual order for dragging
  const [items, setItems] = React.useState<any[]>([]);

  // dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    // Position-coloured rows (1v1) are painted by P1/P2 rather than by where
    // they sit, so reordering them is both pointless and misleading.
    if (positionColors) return;

    const { active, over } = event;

    if (over && active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.dragId === active.id);
        const newIndex = items.findIndex((item) => item.dragId === over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // Settings
  const [twoRowMode, setTwoRowMode] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('eb-two-row-player-height') === 'true';
  });

  const [autoSortByFunds, setAutoSortByFunds] = React.useState(() => {
    if (typeof window === 'undefined') return true;
    const stored = localStorage.getItem('eb-auto-sort-by-funds');
    return stored === null ? true : stored === 'true';
  });

  const [playerCardColor, setPlayerCardColor] = React.useState(() => {
    if (typeof window === 'undefined') return '';
    return localStorage.getItem('eb-player-card-color') || '';
  });

  const [outOfMoneyColor, setOutOfMoneyColor] = React.useState(() => {
    if (typeof window === 'undefined') return '#b71c1c';
    return localStorage.getItem('eb-out-of-money-color') || '#b71c1c';
  });

  // Listen for settings changes
  React.useEffect(() => {
    const handleStorageChange = () => {
      setTwoRowMode(localStorage.getItem('eb-two-row-player-height') === 'true');
      setAutoSortByFunds(localStorage.getItem('eb-auto-sort-by-funds') === 'true');
      setPlayerCardColor(localStorage.getItem('eb-player-card-color') || '');
      setOutOfMoneyColor(localStorage.getItem('eb-out-of-money-color') || '#b71c1c');
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('eb-settings-changed', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('eb-settings-changed', handleStorageChange);
    };
  }, []);

  // Prepare the list of teams and placeholders
  const sortedTeams = React.useMemo(() => {
    const baseTeams = [...teams];

    // Position-coloured rows (1v1) arrive already ordered P1 then P2, and the
    // caller needs that order preserved. Reordering here would silently swap
    // which player a card belongs to, so both of the reorders below are skipped.
    if (positionColors) {
      // Intentionally left in the order the caller supplied.
    } else if (autoSortByFunds) {
      // Auto-sort by funds if enabled
      baseTeams.sort((a, b) => {
        const aFunds = a.budget_remaining ?? 0;
        const bFunds = b.budget_remaining ?? 0;
        return bFunds - aFunds; // Sort descending (highest funds first)
      });
    } else if (currentUserId) {
      // Default behavior: put current user first
      const myTeamIdx = baseTeams.findIndex(t => t?.user_id === currentUserId);
      if (myTeamIdx > 0) {
        const [myTeam] = baseTeams.splice(myTeamIdx, 1);
        baseTeams.unshift(myTeam);
      }
    }

    // Map existing teams to objects with a stable dragId, then pad with placeholders
    const padded = baseTeams.map((t, i) => ({ ...t, dragId: t.user_id || `team-${i}` }));
    while (padded.length < numPlayers) {
      padded.push({ isPlaceholder: true, dragId: `placeholder-${padded.length}` } as any);
    }
    return padded;
  }, [teams, currentUserId, numPlayers, autoSortByFunds, positionColors]);

  // Sync local items with props when the underlying data changes (e.g. someone joins/leaves)
  React.useEffect(() => {
    setItems(prevItems => {
      const prevIds = new Set(prevItems.map(i => i.dragId));
      const nextIds = new Set(sortedTeams.map(i => i.dragId));

      const idsChanged = prevIds.size !== nextIds.size || sortedTeams.some(t => !prevIds.has(t.dragId));

      // If structure changed (ids added/removed) or first load, reset to default sorted order
      if (prevItems.length === 0 || idsChanged) {
        return sortedTeams;
      }

      // If auto-sort is enabled, reorder items to match sorted order using arrayMove for animation
      if (autoSortByFunds && !positionColors) {
        let newOrder = [...prevItems];
        // Apply moves to transform current order to sorted order
        for (let targetIndex = 0; targetIndex < sortedTeams.length; targetIndex++) {
          const targetItem = sortedTeams[targetIndex];
          const currentIndex = newOrder.findIndex(item => item.dragId === targetItem.dragId);
          if (currentIndex !== -1 && currentIndex !== targetIndex) {
            newOrder = arrayMove(newOrder, currentIndex, targetIndex);
          }
        }
        // Update the data in the reordered items
        return newOrder.map(item => {
          const freshData = sortedTeams.find(t => t.dragId === item.dragId);
          return freshData ? { ...item, ...freshData } : item;
        });
      }

      // Maintain the manual order by mapping current items to their updated data
      return prevItems.map(item => {
        const freshData = sortedTeams.find(t => t.dragId === item.dragId);
        return freshData ? { ...freshData } : item;
      });
    });
  }, [sortedTeams, autoSortByFunds, positionColors]);

  React.useEffect(() => {
    if (isInitial.current) {
      isInitial.current = false;
      return;
    }

    if (highestBid && highestBid > 0 && highestBidderId) {
      setAnimatingId(highestBidderId);
      const timer = setTimeout(() => setAnimatingId(null), 500);
      return () => clearTimeout(timer);
    }
  }, [highestBid, highestBidderId]);

  const getIconName = (name: string) => {
    if (name.toLowerCase().startsWith('egg')) return 'egg';
    return name.toLowerCase();
  };

  // Resolves a card's P1/P2 colour from which team it actually is, never from
  // where it happens to sit in the row. Keying this off the render index was
  // the source of a bug where 1v1 players saw each other's colours, because
  // the row reorders itself (auto-sort by funds, "me first", dragging).
  const getPositionColorClass = (team: any, idx: number): string => {
    if (!positionColors || team.isPlaceholder) return '';
    if (positionOneId) {
      const isP1 = team.user_id === positionOneId || team.guest_id === positionOneId;
      return isP1 ? 'player-slot-p1' : 'player-slot-p2';
    }
    // No P1 id supplied, so fall back to the caller's ordering.
    return idx === 0 ? 'player-slot-p1' : idx === 1 ? 'player-slot-p2' : '';
  };

  const containerClassName = `auction-players-row ${twoRowMode ? 'two-row-mode' : ''} ${equalWidth ? 'equal-width' : ''}${highlightId ? ' players-turn-highlight' : ''}`;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={!twoRowMode ? [restrictToHorizontalAxis] : undefined}
    >
      <SortableContext
        items={items.map(item => item.dragId)}
        strategy={horizontalListSortingStrategy}
      >
        <div className={containerClassName} style={{ ['--out-of-money-color' as any]: outOfMoneyColor }}>
          {items.map((team, idx) => (
            <SortableItem
              key={team.dragId}
              team={team}
              highestBidderId={highestBidderId}
              wsConnected={wsConnected}
              animatingId={animatingId}
              autoSortByFunds={autoSortByFunds}
              getIconName={getIconName}
              auctionCompleted={auctionCompleted}
              hideMoney={hideMoney}
              positionColorClass={getPositionColorClass(team, idx)}
              highlighted={!!highlightId && (team.user_id === highlightId || team.guest_id === highlightId)}
              currentUserId={currentUserId}
              playerCardColor={playerCardColor}
              positionColors={positionColors}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default PlayerRow;
